import { apiRequest, apiRequestErrorToError } from 'helpers/api';

import { ProgressRequestError } from './progressError';
import type { ProgressErrorBody } from './progressError';

export const ONBOARDING_PROGRESS_QUERY_KEY = 'onboardingProgress';

// the GET and the PUT's cache write share it, so the key's shape lives in one place
export function onboardingProgressQueryKey(restaurantId: string | undefined) {
  return [ONBOARDING_PROGRESS_QUERY_KEY, restaurantId];
}

export type JsonObject = Record<string, unknown>;

export type ProgressStepId =
  | 'brand_assets'
  | 'delivery'
  | 'menu_builder'
  | 'order_intake'
  | 'payment_setup'
  | 'point_of_sale'
  | 'sales_tax'
  | 'website_setup';

export type ProgressStepStatus = 'completed' | 'incomplete' | 'pending';

export type ProgressStep = {
  id: ProgressStepId;
  metadata: JsonObject;
  name: string;
  status: ProgressStepStatus;
};

/** Snapshot returned by both GET and PUT `restaurants/{id}/progress`. */
export type OnboardingProgress = {
  is_completed: boolean;
  restaurant_id: string;
  steps: {
    recommended: ProgressStep[];
    required: ProgressStep[];
  };
};

/** The step with this id, whichever rail it is on. */
export function findProgressStep(
  progress: OnboardingProgress | undefined,
  stepId: ProgressStepId,
): ProgressStep | undefined {
  return [...(progress?.steps.required ?? []), ...(progress?.steps.recommended ?? [])].find(
    ({ id }) => id === stepId,
  );
}

/** `metadata` omitted or `null` keeps the step's metadata, `{}` clears it, else replaces it. */
export type ProgressUpdate = {
  metadata?: JsonObject | null;
  status: ProgressStepStatus;
  step_id: ProgressStepId;
};

export type ProgressClient = {
  get: (restaurantId: string) => Promise<OnboardingProgress>;
  put: (restaurantId: string, update: ProgressUpdate) => Promise<OnboardingProgress>;
};

function isProgressErrorBody(payload: unknown): payload is ProgressErrorBody {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'details' in payload &&
    (payload.details === null || Array.isArray(payload.details)) &&
    'error' in payload &&
    typeof payload.error === 'string' &&
    'message' in payload &&
    typeof payload.message === 'string'
  );
}

function progressPath(restaurantId: string) {
  return `proxy/onboarding-orchestrator/restaurants/${restaurantId}/progress`;
}

// Hermosa's own 401 (an expired session) never reaches the orchestrator, so it has no error body
const UNAUTHORIZED_BODY: ProgressErrorBody = {
  details: null,
  error: 'unauthorized',
  message: 'Authentication required',
};

async function requestProgress(
  restaurantId: string,
  update?: ProgressUpdate,
): Promise<OnboardingProgress> {
  let requestError: ProgressRequestError | undefined;

  const { data, error } = await apiRequest<OnboardingProgress>({
    path: progressPath(restaurantId),
    ...(update && { method: 'PUT', params: update }),
    // apiRequest swaps 401/403/5xx bodies for a generic error, so read the status and body here
    onError: (response, _error, payload) => {
      if (!response) return;

      if (isProgressErrorBody(payload)) {
        requestError = new ProgressRequestError(response.status, payload);
      } else if (response.status === 401) {
        requestError = new ProgressRequestError(401, UNAUTHORIZED_BODY);
      }
    },
  });

  if (error) throw requestError ?? apiRequestErrorToError(error);

  return data;
}

export const orchestratorProgressClient: ProgressClient = {
  get: restaurantId => requestProgress(restaurantId),
  put: requestProgress,
};
